import { Link } from 'react-router-dom';
import { useState } from 'react';
import type { LessonModel } from '../../../../Models/LessonModel';
import type { StudentModel } from '../../../../Models/StudentModel';
import { useTranslation } from '../../../../i18n/useTranslation';
import { StudentKind } from '../../../../Models/enums';
import { contactDisplayName, contactDisplayRelation, studentDisplayAddress, studentDisplayName, studentDisplayNotes } from '../../../../Utils/DisplayNameUtil';
import { WEEKDAY_KEYS } from '../../../../i18n/weekdays';
import { amountValue } from '../../../../Utils/MoneyUtil';
import { isCollected, isFreeCancel, isOwed, isScheduledLesson } from '../../../../Utils/LessonStatusUtil';
import { routes, toPath } from '../../../../Utils/routes';
import { whatsAppUrl } from '../../../../Utils/WhatsAppUtil';
import { MoneyText } from '../../../SharedArea/MoneyText/MoneyText';
import { DebtReminderAction } from '../DebtReminderAction/DebtReminderAction';
import './StudentCard.css';

interface StudentCardProps {
  student: StudentModel;
  lessons: readonly LessonModel[];
  onEdit: (student: StudentModel) => void;
  onDelete: (student: StudentModel) => void;
  onQuickAdd: (student: StudentModel) => void;
  onMultiAdd: (student: StudentModel) => void;
  onMarkAllPaid: (student: StudentModel, owed: readonly LessonModel[]) => void;
  onGcalChange: (student: StudentModel, enabled: boolean) => Promise<void>;
}

/**
 * The roster's working card. It answers the questions a tutor asks before
 * opening a student's full page: who this is, when they usually learn, and
 * whether the money is collected, owed, or still ahead.
 */
export function StudentCard({ student, lessons, onEdit, onDelete, onQuickAdd, onMultiAdd, onMarkAllPaid, onGcalChange }: StudentCardProps) {
  const { tr, language } = useTranslation();
  const totals = totalsFor(student, lessons);
  const owedLessons = lessons.filter(lesson => isOwed(lesson, student.slots));
  // What this reader should see, which is not always what is stored. Every
  // link and request below still uses `student.name`, because that is the
  // identity and it does not change with the interface language.
  const displayName = studentDisplayName(student, language);
  const displayAddress = studentDisplayAddress(student, language);
  const displayNotes = studentDisplayNotes(student, language);
  const organization = student.kind === StudentKind.Organization;
  const initial = displayName.trim().charAt(0);
  const [copied, setCopied] = useState(false);
  const [gcalSaving, setGcalSaving] = useState(false);

  async function copyEntryCode() {
    if (!student.entryCode) return;
    try {
      await navigator.clipboard.writeText(student.entryCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access is browser-controlled. The code stays visible to copy manually.
    }
  }

  async function changeCalendar(enabled: boolean) {
    setGcalSaving(true);
    try {
      await onGcalChange(student, enabled);
    } catch {
      // The controlled checkbox remains on its last confirmed server value.
    } finally {
      setGcalSaving(false);
    }
  }

  return (
    <li className={`student-card${student.archived ? ' is-archived' : ''}`}>
      <header className="student-card-header">
        <span className="student-avatar" aria-hidden="true">{initial}</span>
        <div className="student-card-identity">
          <h2>{displayName}</h2>
          <p><MoneyText amount={student.price} /> {tr('price')}</p>
        </div>
        {organization && <span className="student-archived-badge">{tr('organizationBadge')}</span>}
        {student.archived === 1 && <span className="student-archived-badge">{tr('archived')}</span>}
      </header>

      <div className="student-stat-grid" aria-label={displayName}>
        <Stat value={lessons.length} label={tr('navLessons')} />
        <Stat value={<MoneyText amount={totals.collected} />} label={tr('income')} tone="collected" />
        <Stat value={<MoneyText amount={totals.owed} />} label={tr('debt')} tone={totals.owed > 0 ? 'owed' : undefined} />
        {totals.scheduled > 0 && <Stat value={<MoneyText amount={totals.scheduled} />} label={tr('scheduledIncome')} tone="scheduled" />}
      </div>

      {totals.owed > 0 && (
        <div className="student-debt-notice">
          <span aria-hidden="true">!</span>
          <span><MoneyText amount={totals.owed} /> {tr('debt')}</span>
          <DebtReminderAction student={student} lessons={owedLessons} />
        </div>
      )}

      <div className="student-card-details">
        {!organization && (
          <div className="student-slot-list">
            {student.slots.length > 0 ? student.slots.map(item => (
              <span key={item.id} className="student-slot-chip"><bdi>{tr(WEEKDAY_KEYS[item.weekday])} {item.time}</bdi></span>
            )) : <span className="student-slot-chip is-empty">—</span>}
          </div>
        )}
        {student.contacts.filter(contact => contact.phone).map(contact => (
          <div key={contact.id} className="student-contact-row">
            <a className="student-card-detail student-contact" href={`tel:${contact.phone}`} title={tr('phone')}><span aria-hidden="true">◉</span><span>{contactDisplayName(contact, language)}{contactDisplayRelation(contact, language) ? ` · ${contactDisplayRelation(contact, language)}` : ''}</span></a>
            {contact.receivesPayments === 1 && <span className="student-contact-pays" title={tr('navPayments')}>₪</span>}
            <a className="student-whatsapp" href={whatsAppUrl(contact.phone, '') ?? undefined} target="_blank" rel="noreferrer" aria-label="WhatsApp">◔</a>
          </div>
        ))}
        {student.phone && <a className="student-card-detail student-contact" href={`tel:${student.phone}`}><span aria-hidden="true">◉</span><bdi dir="ltr">{student.phone}</bdi></a>}
        {displayAddress && <a className="student-card-detail student-address" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`} target="_blank" rel="noreferrer"><span aria-hidden="true">⌖</span><span>{displayAddress}</span></a>}
        {student.entryCode && <button type="button" className="student-entry-code" onClick={() => void copyEntryCode()}><span aria-hidden="true">⌘</span><bdi>{copied ? tr('copied') : student.entryCode}</bdi></button>}
        {displayNotes && <p className="student-notes"><span aria-hidden="true">✦</span>{displayNotes}</p>}
        {/* Only worth saying once there is something to say. A tutor watches
            this number when a student starts cancelling regularly. */}
        {totals.freeCancels > 0 && (
          <p className="student-free-cancels">
            <span aria-hidden="true">⊘</span>
            <span>{tr('freeCancels')}: <bdi>{totals.freeCancels}</bdi></span>
          </p>
        )}
      </div>

      {!organization && (
        <label className="student-gcal-toggle">
          <input type="checkbox" checked={student.gcalRecurring === 1} disabled={gcalSaving} onChange={event => void changeCalendar(event.target.checked)} />
          <span>{tr('navCalendar')}</span>
        </label>
      )}

      <footer className="student-card-actions">
        <Link
          className="student-icon-button"
          to={toPath(routes.studentProfile, { name: student.name })}
          title={tr('openStudent')}
          aria-label={tr('openStudent')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2.2"/><path d="M5.5 17c.5-1.8 1.9-2.8 3.5-2.8s3 1 3.5 2.8M15 9h4M15 13h4"/></svg>
        </Link>
        <button type="button" className="student-card-primary" onClick={() => onQuickAdd(student)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
          <span>{tr('addLesson')}</span>
        </button>
        <button type="button" className="student-icon-button" onClick={() => onMultiAdd(student)} title={tr('addManyDates')} aria-label={tr('addManyDates')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4M12 13v5M9.5 15.5h5"/></svg>
        </button>
        {/* Only what is genuinely owed, never a lesson still ahead. Marking a
            future lesson paid is how income appears before it is earned. */}
        <button
          type="button"
          className="student-icon-button is-collect"
          onClick={() => onMarkAllPaid(student, owedLessons)}
          disabled={owedLessons.length === 0}
          title={tr('markAllPaid')}
          aria-label={tr('markAllPaid')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 12.5 5 5L20 7"/></svg>
        </button>
        <Link className="student-icon-button" to={routes.receipts} title={tr('navReceipts')} aria-label={tr('navReceipts')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6M9 16h4"/></svg>
        </Link>
        {/* History means this student's lessons, so the ledger opens already
            narrowed to them rather than on everyone's. */}
        <Link
          className="student-icon-button"
          to={`${routes.payments}?student=${encodeURIComponent(student.name)}`}
          title={tr('history')}
          aria-label={tr('history')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
        </Link>
        <button type="button" className="student-icon-button" onClick={() => onEdit(student)} title={tr('edit')} aria-label={tr('edit')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
        <button type="button" className="student-icon-button is-danger" onClick={() => onDelete(student)} title={tr('delete')} aria-label={tr('delete')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13"/></svg>
        </button>
      </footer>
    </li>
  );
}

function Stat({ value, label, tone }: { value: React.ReactNode; label: string; tone?: 'collected' | 'owed' | 'scheduled' }) {
  return <div className={`student-stat${tone ? ` is-${tone}` : ''}`}><strong>{value}</strong><span>{label}</span></div>;
}

function totalsFor(student: StudentModel, lessons: readonly LessonModel[]) {
  return lessons.reduce(
    (totals, lesson) => {
      const amount = amountValue(lesson.amount);
      if (isFreeCancel(lesson)) totals.freeCancels += 1;
      else if (isCollected(lesson)) totals.collected += amount;
      else if (isOwed(lesson, student.slots)) totals.owed += amount;
      else if (isScheduledLesson(lesson, student.slots)) totals.scheduled += amount;
      return totals;
    },
    { collected: 0, owed: 0, scheduled: 0, freeCancels: 0 }
  );
}
